import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import MaestroNavbar from "./blocks/MaestroNavbar.js";
import MaestroFooter from "./blocks/MaestroFooter.js";
import Seo from "./blocks/Seo.js";

/**
 * Terms of Service.
 *
 * Adapted from the alfie/stanza-react-app template for Maestro's K-12
 * STEM education context. Every reference to "Stanza", "calendar",
 * "Calendar Content", and "SpotOn Computing" was replaced with the
 * Maestro equivalents.
 *
 * Placeholders that need legal review before launch:
 *   - Entity name ("Lemonade Labs LLC") and any 501(c)(3) status
 *   - Registered office address
 *   - DMCA / notice contact email
 *   - Governing-law jurisdiction (currently California)
 */
const Terms = () => {
  const theme = useTheme();

  const Heading = ({ children }) => (
    <Typography
      component="h2"
      sx={{
        fontSize: "1.375rem",
        fontWeight: 700,
        letterSpacing: "-0.01em",
        mt: 5,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );

  const P = ({ children }) => (
    <Typography
      component="p"
      sx={{
        fontSize: "1rem",
        color: theme.palette.paper.primary,
        lineHeight: 1.65,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );

  const UL = ({ children }) => (
    <Box
      component="ul"
      sx={{
        m: 0,
        mb: 2,
        pl: 3,
        // Force <li> to inherit the theme's font family. Without this,
        // raw <li> elements fall back to the browser default (Times /
        // system serif) and read as a different typeface than the
        // MUI Typography paragraphs above/below.
        fontFamily: theme.typography.fontFamily,
        "& li": {
          fontFamily: "inherit",
          fontSize: "1rem",
          color: theme.palette.paper.primary,
          lineHeight: 1.65,
          mb: 1,
        },
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#000000",
        color: theme.palette.paper.primary,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Seo
        title="Terms of Service · Maestro"
        description="Terms of Service for Maestro, AI + coaching for K-12 STEM teachers."
      />

      <MaestroNavbar variant="dark" />

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, flexGrow: 1 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "2rem", md: "2.75rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.1,
            mb: 1,
            color: theme.palette.paper.primary,
          }}
        >
          Terms of Service
        </Typography>
        <Typography
          sx={{
            fontSize: "0.9375rem",
            color: "rgba(255,255,255,0.6)",
            mb: 5,
          }}
        >
          Last updated: July 2, 2026
        </Typography>

        <P>
          These Terms of Service ("Terms") govern your access to and use of the
          Maestro platform, website, and any related services (collectively,
          the "Platform") provided by Lemonade Labs LLC. ("Maestro,"
          "we," "us," or "our"). The Platform provides AI-generated teaching
          materials and coaching support for K-12 STEM educators.
        </P>
        <P>
          By accessing or using the Platform, you agree to be bound by these
          Terms. If you are using the Platform on behalf of a school,
          district, or other organization, you represent that you have the
          authority to bind that organization to these Terms.
        </P>
        <P>
          We may revise these Terms from time to time and will post the most
          current version on our website. If a revision meaningfully impacts
          you, we will notify you by email at the address associated with
          your account. Continued use of the Platform after the effective
          date of any revision constitutes your acceptance of the revised
          Terms.
        </P>
        <P>
          Please review our{" "}
          <Box
            component="a"
            href="/privacy"
            sx={{ color: theme.palette.success.main, fontWeight: 600 }}
          >
            Privacy Policy
          </Box>{" "}
          for details on how we collect, use, and share information related
          to your use of the Platform, including student and teacher data.
        </P>

        <Heading>1. Who can use the Platform</Heading>
        <P>
          The Platform is intended for use by educators, school
          administrators, and district partners working with students in
          grades K-12. You must be at least 18 years old to create an
          account. Students may only access outputs of the Platform (such as
          scaffolded worksheets or study guides) through their teacher, and
          only in accordance with their school's data governance policies and
          any applicable laws, including FERPA and COPPA.
        </P>

        <Heading>2. Accounts and access</Heading>
        <P>
          You are responsible for maintaining the confidentiality of your
          login credentials and for all activity under your account. You
          agree to notify us promptly of any unauthorized access. Maestro
          reserves the right to access your account when necessary to
          respond to your requests for support, investigate potential
          violations of these Terms, or comply with law.
        </P>

        <Heading>3. Your content</Heading>
        <P>
          You retain ownership of any lesson plans, exit tickets, assessment
          data, student rosters, or other content you upload or otherwise
          provide to the Platform ("User Content"). By providing User Content
          to the Platform, you grant Maestro a worldwide, non-exclusive,
          royalty-free license to use, host, process, and display that
          content solely to provide, improve, and support the Platform for
          you and your organization.
        </P>
        <P>
          You are solely responsible for User Content, including for ensuring
          you have all rights necessary to provide it and that its use on
          the Platform does not violate any law, contractual obligation, or
          third-party right (including student privacy laws such as FERPA and
          COPPA where applicable). We do not use identifiable student data
          to train third-party AI models.
        </P>

        <Heading>4. Acceptable use</Heading>
        <P>You agree not to use the Platform to:</P>
        <UL>
          <li>
            Upload or transmit content that infringes any intellectual
            property, privacy, or other rights of any third party.
          </li>
          <li>
            Attempt to gain unauthorized access to the Platform, its related
            systems, or the accounts of other users.
          </li>
          <li>
            Interfere with or disrupt the Platform, including by uploading
            viruses, malicious code, or excessive automated requests.
          </li>
          <li>
            Use the Platform to evaluate students or make high-stakes
            decisions about them without meaningful teacher review.
          </li>
          <li>
            Impersonate any person, misrepresent your affiliation with a
            school or district, or otherwise deceive other users.
          </li>
          <li>
            Violate any applicable local, state, national, or international
            law, including data privacy and export control laws.
          </li>
        </UL>

        <Heading>5. Platform outputs</Heading>
        <P>
          The Platform generates instructional materials (worksheets, study
          guides, reteach materials, gallery walks, and similar) based on
          the User Content and context you provide. Maestro-generated outputs
          are intended as a starting point for a professional educator's
          judgment. You are responsible for reviewing all Platform outputs
          before using them with students. We do not guarantee that any
          output will be accurate, complete, or appropriate for a specific
          classroom, and we disclaim liability for any decisions made
          without teacher review.
        </P>

        <Heading>6. Intellectual property</Heading>
        <P>
          Maestro, its logos, and all Platform features (excluding User
          Content) are owned by Maestro Education, Inc. and are protected by
          copyright, trademark, and other intellectual property laws.
          Subject to these Terms, we grant you a personal, non-transferable,
          non-sublicensable, non-exclusive license to access and use the
          Platform for its intended educational purpose. You may not copy,
          modify, distribute, reverse engineer, or create derivative works
          based on the Platform, other than your own User Content.
        </P>

        <Heading>7. Pricing and fees</Heading>
        <P>
          Individual teachers currently access the Platform at no cost through
          school and district partnerships funded by grants and philanthropic
          contributions. Schools, districts, and other partner organizations
          may enter into a separate partnership agreement with Maestro that
          governs the scope, duration, and financial terms of the engagement.
          These Terms apply in addition to any such agreement.
        </P>

        <Heading>8. Termination</Heading>
        <P>
          You may stop using the Platform at any time. We may suspend or
          terminate your access if we reasonably believe you have violated
          these Terms, if required by law, or if we discontinue the Platform.
          Provisions that by their nature should survive termination
          (including intellectual property provisions, disclaimers,
          limitations of liability, and indemnification obligations) will
          survive.
        </P>

        <Heading>9. Disclaimers</Heading>
        <P>
          The Platform is provided on an "AS IS" and "AS AVAILABLE" basis.
          To the fullest extent permitted by law, Maestro disclaims all
          warranties, express or implied, including warranties of
          merchantability, fitness for a particular purpose, and
          non-infringement. We do not warrant that the Platform will be
          uninterrupted, error-free, or that outputs will meet your specific
          requirements.
        </P>

        <Heading>10. Limitation of liability</Heading>
        <P>
          To the fullest extent permitted by law, Maestro will not be liable
          for any indirect, incidental, special, consequential, or exemplary
          damages, including loss of profits, data, or goodwill, arising out
          of or related to your use of the Platform. Maestro's total
          liability to you for all claims arising out of or related to these
          Terms or the Platform will not exceed the greater of (a) the
          amount you paid Maestro in the 12 months preceding the claim, or
          (b) one hundred U.S. dollars ($100).
        </P>

        <Heading>11. Indemnification</Heading>
        <P>
          You agree to indemnify and hold harmless Maestro and its officers,
          employees, and agents from any claim, loss, or expense (including
          reasonable legal fees) arising out of (i) your use of the
          Platform, (ii) any User Content you provide, or (iii) your
          violation of these Terms or any third-party right.
        </P>

        <Heading>12. Governing law and disputes</Heading>
        <P>
          These Terms are governed by the laws of the State of California,
          without regard to conflict of law principles. You and Maestro
          agree to submit to the exclusive jurisdiction of the state and
          federal courts located in San Francisco County, California, for
          any dispute not subject to informal resolution.
        </P>

        <Heading>13. Copyright complaints (DMCA)</Heading>
        <P>
          If you believe that content on the Platform infringes your
          copyright, please send a notice under the Digital Millennium
          Copyright Act to{" "}
          <Box
            component="a"
            href="mailto:founders@teachwithmaestro.org"
            sx={{ color: theme.palette.success.main, fontWeight: 600 }}
          >
            founders@teachwithmaestro.org
          </Box>{" "}
          (subject: "DMCA Takedown Request"). Your notice must include your
          contact information, a description of the copyrighted work, the
          location of the alleged infringement, and a signed statement made
          under penalty of perjury.
        </P>

        <Heading>14. Contact us</Heading>
        <P>
          If you have questions about these Terms, email us at{" "}
          <Box
            component="a"
            href="mailto:founders@teachwithmaestro.org"
            sx={{ color: theme.palette.success.main, fontWeight: 600 }}
          >
            founders@teachwithmaestro.org
          </Box>
          .
        </P>
      </Container>

      <MaestroFooter variant="dark" />
    </Box>
  );
};

export default Terms;
